import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import CustomInput from '../../common/CustomInput/CustomInput';
import CustomRangeSlider from '../../common/CustomRangeSlider/CustomRangeSlider';
import CustomSelect from '../../common/CustomSelect/CustomSelect';
import CustomToggle from '../../common/CustomToggle/CustomToggle';
import { buildParams, toBool } from '../../core/helpers';
import { useFormInput } from '../../core/hooks/useFormInput';
import OutsideAlerter from '../../core/hooks/useOutsideAlerter';
import { Location, User } from '../../core/models';
import SuggestedLocationItem from '../SuggestionsContainer/SuggestedLocationItem/SuggestedLocationItem';
import SuggestionsContainer from '../SuggestionsContainer/SuggestionsContainer';

const DEFAULT_NEARBY_CHARGER_DISTANCE = 250;
// const MAX_RANGE_OPTION = 2000;
// const RANGE_STEP_SIZE = 10;
const MAX_RATING = '10';
const RATING_STEP_SIZE = '0.1';

let predictionOrderNr = 0;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
let autoCompleteService;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let geocoder: any;

interface Province {
  id: string;
  name: string;
}

interface Country {
  id: string;
  name: string;
}

export default function AdminSearchFilter({ user }: { user: User }) {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const queryParams = {
    sid: searchParams.get('sid'),
    max_distance: Number(searchParams.get('max_distance')),
    use_amenity: toBool(searchParams.get('use_amenity')),
    has_amenity: toBool(searchParams.get('has_amenity')),
    use_ecomov: toBool(searchParams.get('use_ecomov')),
    hide_confirmed: toBool(searchParams.get('hide_confirmed')),
    min_rating: searchParams.get('min_rating'),
    country_id: searchParams.get('country_id') || '129',
    province_id: searchParams.get('province_id') || '6035375',
    lat: searchParams.get('lat'),
    lng: searchParams.get('lng'),
    only_empty_email: toBool(searchParams.get('only_empty_email')),
  };

  // const [ useEcoMov, setUseEcoMov ] = useState(queryParams.use_ecomov);
  // const [ amenities, setAmenities ] = useState(queryParams.has_amenity);
  // const [ useAmenities, setUseAmenities ] = useState(queryParams.use_amenity);
  const [maxDistance] = useState(queryParams.max_distance || DEFAULT_NEARBY_CHARGER_DISTANCE);
  const [countries, setCountries] = useState([]);
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [countryId, setCountryId] = useState(queryParams.country_id);
  const [provinceId, setProvinceId] = useState(queryParams.province_id);
  const [lat, setLat] = useState(queryParams.lat);
  const [lng, setLng] = useState(queryParams.lng);

  // const [sid, setSid] = useState(queryParams.sid || '');
  const [hideConfirmed, setHideConfirmed] = useState(queryParams.hide_confirmed);

  // const [propertyName, setPropertyName] = useState('');
  const sidInput = useFormInput(queryParams.sid || '', () => true);
  const propertyInput = useFormInput(queryParams.sid || '', () => true);
  const [suggestions, setSuggestions] = useState<Location[]>([]);

  const [minRating, setMinRating] = useState(queryParams.min_rating || '8');

  const [showOnlyEmptyEmail, setShowOnlyEmptyEmail] = useState(queryParams.only_empty_email);

  // Init: Fetch countries
  useEffect(() => {
    const isSubscribed = true;

    const fetchInfo = async () => {
      const result = await fetch(`${process.env.REACT_APP_SC_API_BASE}/admin/countries`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });
      const resData = await result.json();

      if (isSubscribed) {
        setCountries(resData);
      }
    };

    fetchInfo().catch(console.error);
  }, []);

  const fetchProvinces = async (cid: string) => {
    const result = await fetch(
      `${process.env.REACT_APP_SC_API_BASE}/admin/provinces?${buildParams({ country_id: cid })}`,
      {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      },
    );
    const resData = await result.json();

    return resData;
  };

  // Fetch provinces
  useEffect(() => {
    const isSubscribed = true;
    setProvinces([]);

    const fetchInfo = async () => {
      const result = await fetchProvinces(countryId);

      if (isSubscribed) {
        setProvinces(result);
      }
    };

    fetchInfo().catch(console.error);
  }, [countryId]);

  // set default province id
  useEffect(() => {
    if (provinces?.length) {
      const p: Province | undefined = provinces.find(
        (e: Province) => e.id === queryParams.province_id,
      );
      if (p) {
        setProvinceId(p.id);
      } else {
        setProvinceId(provinces[0].id);
      }
    }
  }, [provinces, queryParams.province_id]);

  // init google
  useEffect(() => {
    geocoder = new window.google.maps.Geocoder();
    autoCompleteService = new window.google.maps.places.AutocompleteService();
    predictionOrderNr = 0;
  }, [window.google]);

  // const changeDistance = (event: any) => {
  //   setMaxDistance(event.target.value);
  // };
  const changeMinRating = (event: React.FormEvent<HTMLInputElement>) => {
    setMinRating(event.currentTarget.value);
  };
  // const changeUseEcoMov = (event) => {
  //     setUseEcoMov(event.target.checked);
  // };
  const changeHideConfirmed = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHideConfirmed(event.target.checked);
  };

  // const changeAmenities = (event) => {
  //     setAmenities(event.target.checked);
  // };
  // const changeUseAmenities = (event) => {
  //     setUseAmenities(event.target.checked);
  // };

  const changeShowOnlyEmptyEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowOnlyEmptyEmail(event.target.checked);
  };

  const setFilter = () => {
    const params = buildParams({
      max_distance: maxDistance,
      // use_amenity: useAmenities,
      // has_amenity: amenities,
      // use_ecomov: useEcoMov,
      country_id: countryId,
      province_id: provinceId,
      hide_confirmed: hideConfirmed,
      min_rating: minRating,
      only_empty_email: showOnlyEmptyEmail,
      sid: sidInput.value,
      lat: lat,
      lng: lng,
    });

    navigate(`/map?${params}`, {
      replace: true,
    });
  };

  const optionsCountry = countries?.map((e: Country) => (
    <option key={e.id} value={e.id}>
      {e.name}
    </option>
  ));

  const optionsProvinces = [
    <option key={-1} value="">
      -- ALL --
    </option>,
  ].concat(
    provinces?.map((e) => (
      <option key={e.id} value={e.id}>
        {e.name}
      </option>
    )),
  );

  const handleChangeCountry = (evt: React.ChangeEvent<HTMLSelectElement>) => {
    setCountryId(evt.target.value);
  };

  const handleChangeProvince = (evt: React.ChangeEvent<HTMLSelectElement>) => {
    setProvinceId(evt.target.value);
  };

  useEffect(() => {
    setLat('');
    setLng('');
  }, [propertyInput.value]);

  useEffect(() => {
    // let isSubscribed = true;

    const fetchInfo = async () => {
      predictionOrderNr += 1;

      // NOTE(gb) we use a closure to keep track of the order here because Google does not return the callbacks in order.
      // NOTE(gb) we dont use expedia region text search because google search has fuzzy search capabilities which is very useful.
      let formattedSuggestions: Location[] = [];

      if (propertyInput.value) {
        await (async (orderNr) => {
          const url = `${process.env.REACT_APP_SC_API_BASE}/admin/search`;
          const result = await fetch(url, {
            method: 'POST',
            body: JSON.stringify({
              text: propertyInput.value,
            }),
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${user.token}`,
            },
          });
          const resData = await result.json();

          if (result.status === 200) {
            // isSubscribed = false;
            if (orderNr === predictionOrderNr) {
              const formatted = resData.map((e: Location) => ({
                description: e.name,
                place_id: '',
                sid: e._id,
                structured_formatting: {
                  main_text: e.name,
                  secondary_text: `${e.address.city}, ${e.address.country_code}`,
                },
              }));

              formattedSuggestions = formattedSuggestions.concat(formatted);

              setSuggestions(formattedSuggestions);
              return formattedSuggestions;
            }
          }
        })(predictionOrderNr);
        // @TODO FIXME when we get to here, the predictionOrder can have changed again
      }
    };

    fetchInfo().catch(console.error);
  }, [propertyInput.value]);

  const handleLocationOutsideClick = useCallback(() => {
    setSuggestions([]);
  }, []);

  const handlePlaceId = async (prediction: Location) => {
    // NOTE calls into google's geocoder
    const placeId = prediction.place_id;
    const result = await geocoder.geocode({ placeId });
    const place = result.results[0];

    if (place) {
      // const { bounds } = place.geometry;
      // const centerLatLng = bounds.getCenter().lat();
      // const latspan = bounds.toSpan().lat();
      // const lngspan = bounds.toSpan().lng();

      const latlng = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };

      setLat(latlng.lat);
      setLng(latlng.lng);
    }
  };

  const handleSuggestionClick = useCallback((suggestion: Location) => {
    propertyInput.setValue(suggestion.description);
    if (suggestion.sid) {
      sidInput.setValue(suggestion.sid);
    } else if (suggestion.region_id) {
      console.log(suggestion);
    } else {
      sidInput.setValue('');
      handlePlaceId(suggestion);
    }
    setSuggestions([]);
  }, []);

  return (
    <div className="admin-map__filters-container">
      <h2 className="admin-map__filters-title h3">Filter</h2>

      <CustomInput id="sid" label="Supplier ID" name="sid" input={sidInput} placeholder="sid" />

      <div className="">
        <OutsideAlerter onClick={handleLocationOutsideClick} detectTab>
          <div className="desktop-search-option-container location-input">
            <CustomInput
              id="property_name"
              label="Search property"
              name="property_name"
              input={propertyInput}
              placeholder="Property name"
            />
            {suggestions.length ? (
              <SuggestionsContainer suggestionsContainerClass="suggestions-visible">
                <div
                  className="location-search-result-container"
                  style={{ color: 'var(--primary)' }}
                >
                  {suggestions?.map((suggestion) => (
                    <SuggestedLocationItem
                      key={suggestion._id}
                      structured_formatting={suggestion.structured_formatting}
                      prediction={suggestion}
                      onLocationClick={handleSuggestionClick}
                    />
                  ))}
                </div>
              </SuggestionsContainer>
            ) : null}
          </div>
        </OutsideAlerter>
      </div>

      <button
        className="btn btn--grey"
        type="button"
        onClick={() => {
          sidInput.setValue('');
          propertyInput.setValue('');
        }}
      >
        Clear search
      </button>

      <CustomSelect
        id="country"
        label="Country"
        name="country"
        value={countryId}
        onChange={handleChangeCountry}
        options={optionsCountry}
      />
      <CustomSelect
        id="state"
        label="State/Province"
        name="state"
        value={provinceId}
        onChange={handleChangeProvince}
        options={optionsProvinces}
      />
      {/* <CustomToggle
                label="Check against EcoMovement data"
                checked={useEcoMov}
                onChange={changeUseEcoMov}
            />
            {useEcoMov && (
                <>
                    <CustomRangeSlider
                        label="Max. distance to EcoMov location"
                        value={maxDistance}
                        onChange={changeDistance}
                        max={MAX_RANGE_OPTION}
                        step={RANGE_STEP_SIZE}
                        min={0}
                    />
                </>
            )} */}
      {/* <CustomToggle
                label="Filter on Expedia amenity checkbox"
                checked={useAmenities}
                onChange={changeUseAmenities}
            /> */}
      {/* {useAmenities && (
                <CustomToggle
                    label="Has checked the charger amenity"
                    checked={amenities}
                    onChange={changeAmenities}
                />
            )} */}
      <CustomToggle
        label="Hide confirmed"
        checked={hideConfirmed}
        onChange={changeHideConfirmed}
        id="hide-confirmed"
        name="hide-confirmed"
      />

      {true && (
        <>
          <CustomRangeSlider
            id="range"
            name="range"
            label="Choose minimum property rating"
            value={minRating}
            onChange={changeMinRating}
            max={MAX_RATING}
            step={RATING_STEP_SIZE}
            min="0"
          />
          <CustomToggle
            label="Only show properties with no email address"
            checked={showOnlyEmptyEmail}
            onChange={changeShowOnlyEmptyEmail}
            id="no-email"
            name="no-email"
          />
        </>
      )}

      <button className="btn btn--blue" type="button" onClick={setFilter}>
        Set filter
      </button>
    </div>
  );
}
